import defaultImg from '@/assets/img/curved-images/default_ws.jpg';
import bgImg from '@/assets/img/curved-images/top_bg.svg';
import { ApiErrorMessages } from '@/components/element/ApiErrorMessages';
import { HomeLayout } from '@/components/layout/HomeLayout';
import { useWorkspacesInvalidate } from '@/hooks/api/0100_workspaces';
import { useCreateWorkspace } from '@/hooks/api/0110_create_workspace';
import { useBase64FromImageFileInput } from '@/hooks/useBase64FromImageFileInput';
import { useBase64FromUrl } from '@/hooks/useBase64FromUrl';
import { toastSuccess } from '@/lib/toast';
import { useLoading } from '@/providers/LoadingProvider';
import { range } from '@/utils';
import { useEffect } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

type FormInputs = {
  name: string;
  identifier: string;
  closing_month: number;
  logo_image: string;
};

export const WorkspacesNew = () => {
  const navigate = useNavigate();
  const { setShowLoading } = useLoading();
  const { base64Content: defaultImgBase64 } = useBase64FromUrl(defaultImg);
  const { toBase64FromFile, base64Content } = useBase64FromImageFileInput({ aspectRatio: 1 });
  const { mutate, data, isPending, error } = useCreateWorkspace();
  const { invalidate } = useWorkspacesInvalidate();

  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<FormInputs>({
    mode: 'onBlur',
    criteriaMode: 'all',
    defaultValues: {
      closing_month: 3,
    },
  });

  useEffect(() => {
    if (defaultImgBase64) setValue('logo_image', defaultImgBase64);
  }, [defaultImgBase64]);

  useEffect(() => {
    if (base64Content) setValue('logo_image', base64Content);
  }, [base64Content]);

  useEffect(() => {
    setShowLoading(isPending);
  }, [isPending]);

  useEffect(() => {
    if (data) {
      invalidate();
      toastSuccess('スペースの登録が完了しました');
      navigate('/workspaces');
    }
  }, [data]);

  const onSubmit: SubmitHandler<FormInputs> = (data) => {
    setShowLoading(true);
    mutate(data);
  };

  return (
    <HomeLayout>
      <main className="main-content mt-0">
        <div
          className="page-header align-items-start min-vh-50 pt-5 pb-11 m-3 border-radius-lg"
          style={{ backgroundImage: `url(${bgImg})` }}
        >
          <span className="mask bg-gradient-dark opacity-5"></span>
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-lg-5 text-center mx-auto">
                <h1 className="text-white mb-2 mt-5">無料スペースの作成</h1>
              </div>
            </div>
          </div>
        </div>
        <div className="container">
          <div className="row mt-lg-n10 mt-md-n11 mt-n10 justify-content-center">
            <div className="col-xl-10 col-lg-10 col-md-10 mx-auto">
              <div className="card mb-4" id="basic-info">
                <div className="card-header d-flex align-items-center">
                  <div className="col-md-8">
                    <h5 className="mb-1">スペース基本情報</h5>
                  </div>
                </div>
                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="card-body pt-0 row">
                    <ApiErrorMessages error={error} />

                    <div className="col-12 col-sm-4 col-md-3">
                      <div className="avatar avatar-xxl position-relative">
                        <img
                          src={base64Content || defaultImg}
                          alt="profile_image"
                          className="w-100 border-radius-md shadow-sm"
                        />
                        <input
                          type="file"
                          id="avater"
                          accept="image/png, image/jpeg"
                          onChange={toBase64FromFile}
                          style={{ display: 'none' }}
                        />
                        <input type="hidden" {...register('logo_image', {})} />
                        <label
                          className="btn btn-sm btn-icon-only bg-gradient-light position-absolute bottom-0 end-0 mb-n2 me-n2"
                          htmlFor="avater"
                        >
                          <i
                            className="fa fa-pen top-0"
                            data-bs-toggle="tooltip"
                            data-bs-placement="top"
                            title=""
                            aria-hidden="true"
                            data-bs-original-title="Edit Image"
                            aria-label="Edit Image"
                          ></i>
                        </label>
                      </div>
                    </div>
                    <div className="col-12 col-sm-8 col-md-9 mt-4 mt-sm-0">
                      <div className="form-group">
                        <label>
                          スペース名<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          maxLength={30}
                          className={`form-control ${errors.name && 'is-invalid'}`}
                          placeholder={'スペース名'}
                          {...register('name', {
                            required: '入力して下さい。',
                          })}
                        />
                        {errors.name && <div className="text-xs text-danger mt-1">{errors.name.message}</div>}
                      </div>
                      <div className="form-group">
                        <label>
                          スペースID<span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          maxLength={30}
                          className={`form-control ${errors.identifier && 'is-invalid'}`}
                          placeholder={'スペースID'}
                          {...register('identifier', {
                            required: '入力して下さい。',
                            pattern: {
                              value: /^\w{3,30}$/,
                              message: '半角英数アンダーバー(_)・3〜30文字で入力して下さい。',
                            },
                          })}
                        />
                        <div className="text-xs text-muted mt-1">半角英数アンダーバー(_)・3〜30文字</div>
                        {errors.identifier && (
                          <div className="text-xs text-danger mt-1">{errors.identifier.message}</div>
                        )}
                      </div>
                      <div className="form-group">
                        <label>
                          決算月<span className="text-danger">*</span>
                        </label>
                        <select className="form-control" {...register('closing_month')}>
                          {range(1, 12).map((m) => (
                            <option key={`month-${m}`} value={m}>
                              {m}月
                            </option>
                          ))}
                        </select>
                        <div className="text-xs text-muted mt-1">決算月を最終月とする一年単位で管理されます。後から変更可能です。</div>
                      </div>
                    </div>
                  </div>
                  <div className="card-footer d-flex align-items-center justify-content-end pt-0">
                    <button className="btn bg-gradient-dark btn-icon">
                      <i className="fas fa-check-circle"></i>新規作成する
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </main>
    </HomeLayout>
  );
};
